import React, { useState, useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from "react-router-dom";
import {Helmet} from "react-helmet";
import TagManager from 'react-gtm-module'

import {ConsentModal} from "./pages/ConsentModal";

import TensorAICommand from "./pages/Home/TensorAICommand";
import Footer from './sharedcomponents/footer/Footer'
import PrivateRoute from "./sharedcomponents/PrivateRoute"
import { AuthProvider } from "./contexts/AuthContext"
import SiteNavigationSchema from './sharedcomponents/SiteNavigationSchema'
import OrganizationSchema from './sharedcomponents/OrganizationSchema'
import SitelinksSearchBoxSchema from './sharedcomponents/SitelinksSearchBoxSchema'
import ProductSchema from './sharedcomponents/ProductSchema'
import SocialProfileSchema from './sharedcomponents/SocialProfileSchema'
import BreadcrumbSchema from './sharedcomponents/BreadcrumbSchema'
import LogoLoader from './components/LogoLoader';
import RouteLoadingFallback from './components/RouteLoadingFallback';
import Logo from './assets/Logo.png';
import './App.css'


// Lazy-loaded components
// Public Routes
const Sales = React.lazy(() => import('./pages/Sales'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const Signup = React.lazy(() => import('./pages/Signup'));
const Login = React.lazy(() => import('./pages/Login'));
const ElectronLogin = React.lazy(() => import('./pages/ElectronLogin'));
const DownloadPage = React.lazy(() => import('./pages/Download/DownloadPage'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const AuthAction = React.lazy(() => import('./pages/AuthAction'));
const TermsAndConditions = React.lazy(() => import('./pages/Legal/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/Legal/PrivacyPolicy'));
const NotFound = React.lazy(() => import('./pages/NotFound/NotFound'));
const Unsubscribe = React.lazy(() => import('./pages/Unsubscribe/Unsubscribe'));
const Search = React.lazy(() => import('./pages/Search/Search'));
const FAQ = React.lazy(() => import('./pages/FAQ/FAQ'));
const Career = React.lazy(() => import('./pages/Career'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const AIModelsPage = React.lazy(() => import('./pages/AIModels/AIModelsPage'));
const WhyTensorPilotPage = React.lazy(() => import('./pages/Home/ArchitecturePage'));
const ScheduleEnterprise = React.lazy(() => import('./pages/Schedule'));

// Private Routes
const PaymentFailure = React.lazy(() => import('./pages/PaymentFailure'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Blog = React.lazy(() => import('./pages/Blog/Blog'));
const BlogPost = React.lazy(() => import('./pages/Blog/BlogPost'));

const App = (props) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tagManagerInitialized, setTagManagerInitialized] = useState(false);

  useEffect(() => {
    const consentGiven = localStorage.getItem('userConsent');
    if (!consentGiven) {
      setIsModalVisible(true);
    } else if (consentGiven === 'true' && !tagManagerInitialized) {
      // initializeTagManager();
      console.log("tag manager initialized in index.html")
    }
  }, [tagManagerInitialized]);

  // Google Tag Manager - gives you the ability to add and update your own tags for conversion tracking, site analytics, remarketing, and more
  const initializeTagManager = () => {
    const tagManagerArgs = {
      gtmId: 'GTM-TP5JMP9M', // Updated to match the ID in index.html
    };
    TagManager.initialize(tagManagerArgs);
    setTagManagerInitialized(true);
  };

  const handleAccept = () => {
    localStorage.setItem('userConsent', 'true');
    setIsModalVisible(false);
    // initializeTagManager();
    console.log("tag manager initialized in index.html")
  };

  const handleDecline = () => {
    localStorage.setItem('userConsent', 'false');
    setIsModalVisible(false);
  };

  return (
  <div className="bg-light">
      <LogoLoader />
      <Helmet>
        {/* Page Title */}
        <title>TensorPilot - Privacy-First AI Desktop Assistant</title>

        {/* Meta Description for SEO */}
        <meta name="description" content="TensorPilot is a privacy-first AI desktop assistant with multi-provider support (OpenAI, Gemini, Claude, Grok). Features live audio sessions, image generation, and advanced AI capabilities - all with your data stored locally." />

        {/* SEO Keywords */}
        <meta name="keywords" content="TensorPilot, Tensor AI, Privacy-First AI, AI Desktop Assistant, Multi-Provider AI, Grok Support, Live Audio AI, OpenAI GPT-4o, Anthropic Claude, Google Gemini, Image Generation, DALL-E Integration, Local AI Processing, Desktop AI Assistant, Enterprise AI Solutions, AI Desktop Applications, Professional AI Tools, AI Video Processing, Private AI, Secure AI Assistant" />

        {/* Open Graph Meta Tags for Social Media */}
        <meta property="og:title" content="TensorPilot - Privacy-First AI Desktop Assistant" />
        <meta property="og:description" content="Privacy-first AI desktop assistant with multi-provider support (OpenAI, Gemini, Claude, Grok). Features live audio sessions, image generation, and local data storage." />
        <meta property="og:url" content="https://www.tensorpilot.io/" />
        <meta property="og:site_name" content="TensorPilot" />
        <meta property="og:image" content="https://www.tensorpilot.io/Logo.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TensorPilot - Privacy-First AI Desktop Assistant" />
        <meta name="twitter:description" content="Privacy-first AI desktop assistant with multi-provider support. Features live audio, Grok support, image generation, and local data storage." />
        <meta name="twitter:image" content="https://www.tensorpilot.io/Logo.png" />

        {/* Additional Meta Tags for Mobile Responsiveness and Compatibility */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://www.tensorpilot.io" />
      </Helmet>

      <SiteNavigationSchema />
      <OrganizationSchema />
      <SitelinksSearchBoxSchema />
      <ProductSchema />
      <SocialProfileSchema />
      <BreadcrumbSchema />
      <ConsentModal 
        show={isModalVisible} 
        handleAccept={handleAccept} 
        handleDecline={handleDecline} 
      />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Router>
          <AuthProvider>
            <Switch>
              <Route exact path="/" component={TensorAICommand} />
              <Redirect exact from="/personal" to="/" />
              <Redirect exact from="/drone-ai" to="/" />
              <Redirect exact from="/vision-ai" to="/" />
              {/* <Route exact path="/team" component={Team} /> */}
              <Route exact path="/sales" component={Sales} />          
              <Route exact path="/contactus" component={ContactUs} />
              <Route exact path="/download" component={DownloadPage} />
              {/* <Route exact path="/investors" component={Investors} /> */}
              <Route exact path="/signup" component={Signup} />
              <Route exact path="/blog" component={Blog} />
              <Redirect
                exact
                from="/blog/thirty-dollar-seat-trap-byok-scale-enterprise-ai"
                to="/blog/ninety-nine-seat-tax-byok-enterprise-ai"
              />
              <Route path="/blog/:slug" component={BlogPost} /> {/* Updated to use slug instead of ID */}
              <Route exact path="/login" component={Login} />
              <Route exact path="/electron-login" component={ElectronLogin} />
              <Route exact path="/forgot-password" component={ForgotPassword} />
              {/* Custom Firebase email action handler (password reset, email verification, email recovery) */}
              <Route exact path="/auth/action" component={AuthAction} />
              <Route exact path="/download" component={DownloadPage} />
              {/* <Route exact path="/softwareguide" component={SoftwareGuide} /> */}
              <PrivateRoute path="/paymentsuccess" component={PaymentSuccess} /> 
              <PrivateRoute path="/paymentfailure" component={PaymentFailure} /> 
              <PrivateRoute path="/profile" component={Profile} /> 
              <Route path="/terms-and-conditions" component={TermsAndConditions} /> 
              <Route path="/privacy-policy" component={PrivacyPolicy} /> 
              <Route path="/unsuscribe" component={Unsubscribe} /> 
              <Route path="/search" component={Search} />
              <Route exact path="/faq" component={FAQ} />
              <Route exact path="/careers" component={Career} />
              <Route exact path="/about" component={AboutUs} />
              <Redirect exact from="/models" to="/ai-models" />
              <Route exact path="/ai-models" component={AIModelsPage} />
              <Redirect exact from="/architecture" to="/why-tensorpilot" />
              <Route exact path="/why-tensorpilot" component={WhyTensorPilotPage} />
              <Route exact path="/demo" component={ScheduleEnterprise} />
              <Redirect exact from="/schedule" to="/demo" />
              <Route component={NotFound} />
              {/* <PrivateRoute exact path="/downloads" component={Downloads} /> */}
            </Switch>
            <Footer />
          </AuthProvider>
        </Router>
      </Suspense>
  </div>
  );
}


export default App;
